import { setContext } from "@sentry/gatsby";
import { Script } from "gatsby";
import {
	useEffect,
	useRef,
	useState,
	type FC,
	type PropsWithChildren,
} from "react";
import SiteSelectModalContainer from "../../components/SiteSelectModal/SiteSelectModalContainer";
import ZendeskWidget from "../../components/Zendesk/ZendeskWidget";
import SiteFooterContainer from "../../containers/footer/footer.container";
import SiteHeaderContainer from "../../containers/header/header.container";
import { AppDispatchContext, useAppState } from "../../context/app/context";
import { initStoryblokApi } from "../../gatsby-utils/helpers/storyblok-client";
import { useCartService } from "../../hooks/use-cart-service";
import { useDispatchContext } from "../../hooks/use-dispatch-context";
import useKlaviyo from "../../hooks/use-klaviyo";
import { useOrderService } from "../../hooks/use-order-service";
import { useUserLocation } from "../../hooks/use-user-location";
import type { ExpandedOrder } from "../../utils/commercetools/order-service";
import { getUserCountry, isValidCountryCode } from "../../utils/country";
import { pushCustomGTMEvent } from "../../utils/gtm/gtm-handlers";
import { updateGtmVariables } from "../../utils/gtm/use-gtm-variables";
import { useAwin } from "../../utils/paid-socials/awin/use-awin";
import { useCj } from "../../utils/paid-socials/cj/use-cj";
import { useGrin } from "../../utils/paid-socials/grin/use-grin";
import { useMeta } from "../../utils/paid-socials/meta/use-meta";
import { useTikTok } from "../../utils/paid-socials/tiktok/tiktok";
import { getQueryStringParam } from "../../utils/query-string";
import { isInPreviewEditor } from "../../utils/storyblok/is-in-preview-editor";
import PageFoot from "../page-foot";

type PageProps = PropsWithChildren<{
	showQuickSearch?: boolean;
	title?: string | null;
	description?: string | null;
	isStartPage?: boolean | null;
	noIndex?: boolean | null;
	noFollow?: boolean | null;
	hideFooter?: boolean;
	showCart?: boolean;
}>;

const handleFailedScriptLoad = (id: string, error: ErrorEvent) => {
	console.warn(`Failed to load script: ${id} with error: ${error.message}`);

	setContext("Failed external scripts", {
		[id]: error.message,
	});
};

const PageContainerComponent: FC<PageProps> = ({
	hideFooter,
	showCart,
	showQuickSearch = true,
	children,
}) => {
	const { scripts, userCountry, allowedCountries } = useAppState();

	const allowedCountriesRef = useRef(allowedCountries);

	const appDispatch = useDispatchContext(AppDispatchContext);

	initStoryblokApi(isInPreviewEditor());
	const orderId = getQueryStringParam("order");
	const [order, setOrder] = useState<ExpandedOrder>();
	const { getOrder } = useOrderService();
	const { cart, hasCart, cartLoading, initExistingCart } = useCartService();

	const {
		data: userLocationData,
		isLoading: isUserLocationLoading,
		isError: isUserLocationError,
	} = useUserLocation();

	const [showSiteSelector, setShowSiteSelector] = useState(false);

	const recommendedCountry =
		userLocationData?.code &&
		isValidCountryCode(userLocationData.code) &&
		!allowedCountriesRef.current.includes(userLocationData.code)
			? userLocationData.code
			: undefined;

	useEffect(() => {
		if (!hasCart && cartLoading) {
			void initExistingCart();
		}
	}, [hasCart, cartLoading, initExistingCart]);

	useEffect(() => {
		if (!userCountry) {
			// Check if the user has a country set in the URL or in the cookie
			const requestedCountry = getUserCountry(allowedCountriesRef.current);
			if (requestedCountry) {
				appDispatch({
					type: "setUserCountry",
					userCountry: requestedCountry,
				});

				return;
			}
		}

		//TODO: (JAPAN) Remove this "if" once Japan is live
		if (
			window.location.hostname.includes("preview") ||
			window.location.hostname.includes("localhost")
		) {
			setShowSiteSelector(false);
		} else if (!userCountry && !isUserLocationLoading) {
			if (userLocationData?.code) {
				if (
					isValidCountryCode(userLocationData.code) &&
					allowedCountriesRef.current.includes(userLocationData.code)
				) {
					appDispatch({
						type: "setUserCountry",
						userCountry: userLocationData.code,
					});
				} else {
					setShowSiteSelector(true);
				}
			} else if (isUserLocationError) {
				// Handle error case
				if (allowedCountriesRef.current.length === 1) {
					appDispatch({
						type: "setUserCountry",
						userCountry: allowedCountriesRef.current[0],
					});
				} else {
					setShowSiteSelector(true);
				}
			}
		} else if (userCountry && showSiteSelector) {
			setShowSiteSelector(false);
		}
	}, [
		appDispatch,
		showSiteSelector,
		userCountry,
		userLocationData?.code,
		isUserLocationLoading,
		isUserLocationError,
	]);

	useEffect(() => {
		if (orderId) {
			void getOrder(orderId).then((order) => {
				setOrder(order);
			});
		}
	}, [getOrder, orderId]);

	useEffect(() => {
		if (hasCart) {
			updateGtmVariables({
				order,
				cart,
			});
		}
	}, [order, cart, hasCart]);

	useAwin();
	useCj();
	useKlaviyo();
	useTikTok();
	useMeta();
	useGrin();

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				pushCustomGTMEvent({
					type: "tabHidden",
				});
			} else {
				pushCustomGTMEvent({
					type: "tabVisible",
				});
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	return (
		<>
			<SiteHeaderContainer
				showQuickSearch={showQuickSearch}
				showCart={showCart}
			/>

			{children}
			{[...scripts].map((scriptProps) => (
				<Script
					{...scriptProps}
					key={scriptProps.id}
					onError={(error) => {
						handleFailedScriptLoad(scriptProps.id || "unknown", error);
					}}
					strategy={scriptProps.strategy ?? "idle"}
				/>
			))}

			{showSiteSelector && (
				<SiteSelectModalContainer recommendedCountry={recommendedCountry} />
			)}

			<ZendeskWidget />

			{!hideFooter && <SiteFooterContainer />}
			<PageFoot />
		</>
	);
};

export default PageContainerComponent;
